import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Chip,
  CircularProgress,
  Pagination as MuiPagination,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import ArticleIcon from '@mui/icons-material/Article';
import InfoIcon from '@mui/icons-material/Info';
import ListPageLayout from '../components/shared/list-page-layout';
import ListFilters, { FilterField } from '../components/shared/list-filters';
import { TableSkeleton } from '../components/shared/skeletons';
import { IListPublicationsRequest } from '../../model/base/request/list-publications-request';
import { usePublicationsController } from './context/publications-controller';
import { usePublicationsState } from './context/publications-context';
import { useParams, useSearchParams } from 'react-router-dom';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";

export function PublicationsComponent() {
  const { navigateTo: navigate } = useRoutedNavigation();
  const { categoryId: categoryIdParam, subcategoryId: subcategoryIdParam } = useParams<{ categoryId?: string; subcategoryId?: string }>();
  const [searchParams] = useSearchParams();
  const { loadData, loadCategories, loadSubcategories, clearSubcategories, clearPublications, loadPublishingVehicles } = usePublicationsController();
  const { publications, categories, subcategories, publishingVehicles, loading, loadingFilters } = usePublicationsState();
  const [currentPage, setCurrentPage] = useState(1);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Converte params para números — path params têm prioridade, query string é fallback
  const categoryIdFromUrl = categoryIdParam
    ? parseInt(categoryIdParam, 10)
    : searchParams.get('codCategoria')
      ? parseInt(searchParams.get('codCategoria')!, 10)
      : undefined;
  const subcategoryIdFromUrl = subcategoryIdParam
    ? parseInt(subcategoryIdParam, 10)
    : searchParams.get('codSubcategoria')
      ? parseInt(searchParams.get('codSubcategoria')!, 10)
      : undefined;

  const [filters, setFilters] = useState<IListPublicationsRequest>({
    page: 0,
    pageSize: 12,
    title: '',
    categoryId: categoryIdFromUrl,
    subcategoryId: subcategoryIdFromUrl,
    publishingVehicleId: undefined,
    startDate: '',
    endDate: '',
  });

  // Carregar categorias e veículos ao montar o componente
  useEffect(() => {
    loadCategories();
    loadPublishingVehicles();
  }, []);

  // Carregar subcategorias se categoryId estiver na URL
  useEffect(() => {
    if (categoryIdFromUrl) {
      loadSubcategories(categoryIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIdFromUrl]);

  // Carregar dados quando os parâmetros da URL mudarem (path params ou query string)
  useEffect(() => {
    const updatedFilters = {
      ...filters,
      categoryId: categoryIdFromUrl,
      subcategoryId: subcategoryIdFromUrl,
      page: 0,
    };
    setFilters(updatedFilters);
    setCurrentPage(1);
    clearPublications();
    loadData(updatedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIdParam, subcategoryIdParam, searchParams.get('codCategoria'), searchParams.get('codSubcategoria')]);

  // Opções de filtro dinâmicas
  const categoryOptions = categories
    .filter(cat => cat.id !== undefined)
    .map(cat => ({
      value: cat.id!,
      label: cat.description || `Categoria ${cat.id}`
    }));

  const subcategoryOptions = subcategories
    .filter(subcat => subcat.id !== undefined)
    .map(subcat => ({
      value: subcat.id!,
      label: subcat.description || `Subcategoria ${subcat.id}`
    }));

  const vehicleOptions = publishingVehicles.map(v => ({
    value: v.id,
    label: v.name,
  }));

  const filterFields: FilterField[] = [
    {
      name: 'title',
      label: 'Pesquisar',
      type: 'text',
      placeholder: 'Buscar por descrição',
      gridSize: { xs: 12, sm: 12, md: 12 },
    },
    {
      name: 'categoryId',
      label: 'Categoria',
      type: 'select',
      options: categoryOptions,
      placeholder: 'Selecione uma categoria',
      gridSize: { xs: 12, sm: 6, md: 4 },
    },
    {
      name: 'subcategoryId',
      label: 'Subcategoria',
      type: 'select',
      options: subcategoryOptions,
      placeholder: 'Selecione uma subcategoria',
      disabled: !filters.categoryId || subcategoryOptions.length === 0,
      gridSize: { xs: 12, sm: 6, md: 4 },
    },
    {
      name: 'publishingVehicleId',
      label: 'Veículo de Publicação',
      type: 'select',
      options: vehicleOptions,
      placeholder: 'Selecione um veículo',
      gridSize: { xs: 12, sm: 6, md: 4 },
    },
    {
      name: 'startDate',
      label: 'Data Inicial',
      type: 'date',
      gridSize: { xs: 12, sm: 6, md: 6 },
    },
    {
      name: 'endDate',
      label: 'Data Final',
      type: 'date',
      gridSize: { xs: 12, sm: 6, md: 6 },
    },
  ];

  const handleFilterChange = (name: string, value: any) => {
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        [name]: value,
      };

      // Se a categoria mudou, limpar subcategoria e carregar novas subcategorias
      if (name === 'categoryId') {
        newFilters.subcategoryId = undefined;

        if (value) {
          loadSubcategories(value);
        } else {
          clearSubcategories();
        }
      }

      return newFilters;
    });
  };

  const handleSearch = () => {
    const searchFilters = { ...filters, page: 0 };
    setCurrentPage(1);
    setFilters(searchFilters);
    clearPublications();
    loadData(searchFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: IListPublicationsRequest = {
      page: 0,
      pageSize: 12,
      title: '',
      categoryId: undefined,
      subcategoryId: undefined,
      publishingVehicleId: undefined,
      startDate: '',
      endDate: '',
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    clearSubcategories();

    // Se houver filtros de categoria/subcategoria na URL (path ou query string), redirecionar para a rota sem filtros
    if (categoryIdParam || subcategoryIdParam || searchParams.get('codCategoria') || searchParams.get('codSubcategoria')) {
      navigate('/publicacoes');
    } else {
      loadData(clearedFilters);
    }
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    loadData(newFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleViewDocument = (url?: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleDownloadDocument = (url?: string, description?: string) => {
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = `publicacao-${description || 'documento'}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Obter nomes da categoria e subcategoria selecionadas
  const selectedCategory = categories.find(cat => cat.id === categoryIdFromUrl);
  const selectedSubcategory = subcategories.find(subcat => subcat.id === subcategoryIdFromUrl);

  // Construir título e subtítulo dinâmicos
  const getPageTitle = () => {
    if (selectedSubcategory && selectedCategory) {
      return `Publicações - ${selectedCategory.description} > ${selectedSubcategory.description}`;
    }
    if (selectedCategory) {
      return `Publicações - ${selectedCategory.description}`;
    }
    return "Publicações do Diário Oficial";
  };

  const getPageSubtitle = () => {
    if (selectedSubcategory && selectedCategory) {
      return `Consulte as publicações da categoria "${selectedCategory.description}" na subcategoria "${selectedSubcategory.description}"`;
    }
    if (selectedCategory) {
      return `Consulte as publicações da categoria "${selectedCategory.description}"`;
    }
    return "Consulte as publicações do município";
  };

  const getBreadcrumbs = () => {
    const breadcrumbs: Array<{ label: string; href?: string }> = [
      { label: 'Publicações do Diário Oficial', href: '/publicacoes' }
    ];

    if (selectedCategory) {
      breadcrumbs.push({ label: selectedCategory.description || 'Categoria' });
    }

    if (selectedSubcategory) {
      breadcrumbs.push({ label: selectedSubcategory.description || 'Subcategoria' });
    }

    return breadcrumbs;
  };

  return (
    <ListPageLayout
      title={getPageTitle()}
      subtitle={getPageSubtitle()}
      breadcrumbs={getBreadcrumbs()}
      filters={
        <ListFilters
          fields={filterFields.map((field) => ({
            ...field,
            value: filters[field.name as keyof IListPublicationsRequest],
          }))}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={loading || loadingFilters}
        />
      }
    >
      {loading && !publications.hasItems() ? (
        <TableSkeleton rows={12} columns={5} hasActions={true} />
      ) : !publications.hasItems() ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nenhuma publicação encontrada com os filtros aplicados.
        </Alert>
      ) : (
        <>
          <Box sx={{ position: 'relative' }}>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1000,
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={60} />
              </Box>
            )}
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {publications.items.map((publication) => (
                  <Paper key={publication.id} elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Box
                      sx={{
                        backgroundColor: 'primary.main',
                        px: 2,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <PictureAsPdfIcon sx={{ fontSize: 24, color: 'error.light' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'white', flex: 1 }}>
                        {formatDate(publication.publicationDate)}
                      </Typography>
                      {publication.category && (
                        <Chip
                          label={publication.category}
                          size="small"
                          sx={{ backgroundColor: 'white', color: 'primary.main', fontWeight: 600, maxWidth: 120 }}
                        />
                      )}
                    </Box>
                    <Box sx={{ px: 2, py: 1.5 }}>
                      {publication.publishingVehicle && (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                            Veículo de Publicação
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {publication.publishingVehicle}
                          </Typography>
                        </>
                      )}
                      {publication.description && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                            {publication.description}
                          </Typography>
                        </>
                      )}
                      {(publication.isRepublication || publication.subCategory) && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                          {publication.subCategory && (
                            <Chip label={publication.subCategory} size="small" variant="outlined" color="secondary" />
                          )}
                          {publication.isRepublication && (
                            <Chip label="Republicação" size="small" color="warning" />
                          )}
                        </Box>
                      )}
                    </Box>
                    <Divider />
                    <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Tooltip title="Ver Detalhes" arrow>
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => navigate(`/publicacoes/detalhes/${publication.id}`)}
                          sx={{ '&:hover': { backgroundColor: 'info.main', color: 'white' } }}
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Visualizar PDF" arrow>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDocument(publication.fileViewUrl)}
                          disabled={!publication.fileViewUrl}
                          sx={{ '&:hover': { backgroundColor: 'primary.main', color: 'white' } }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Baixar PDF" arrow>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleDownloadDocument(publication.fileDownloadUrl, publication.description)}
                          disabled={!publication.fileDownloadUrl}
                          sx={{ '&:hover': { backgroundColor: 'success.main', color: 'white' } }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={3}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  opacity: loading ? 0.5 : 1,
                  transition: 'opacity 0.2s ease',
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.main' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem', width: '60px' }}>
                        <ArticleIcon sx={{ verticalAlign: 'middle' }} />
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem', width: '15%' }}>
                        Data de Publicação
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem', width: '15%' }}>
                        Veículo de Publicção
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>
                        Descrição
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem', width: '15%' }}>
                        Categoria
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem', width: '15%' }}>
                        Subcategoria
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem', width: '150px', textAlign: 'center' }}>
                        Ações
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {publications.items.map((publication) => (
                      <TableRow
                        key={publication.id}
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                          '&:hover': { backgroundColor: 'action.selected' },
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PictureAsPdfIcon sx={{ fontSize: 32, color: 'error.main' }} />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(publication.publicationDate)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{publication.publishingVehicle}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: 1.5,
                            }}
                          >
                            {publication.description || '-'}
                          </Typography>
                          {publication.isRepublication && (
                            <Chip label="Republicação" size="small" color="warning" sx={{ mt: 0.5 }} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip label={publication.category || '-'} size="small" variant="outlined" color="primary" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {publication.subCategory || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="Ver Detalhes" arrow>
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => navigate(`/publicacoes/detalhes/${publication.id}`)}
                                sx={{ '&:hover': { backgroundColor: 'info.main', color: 'white' } }}
                              >
                                <InfoIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Visualizar PDF" arrow>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewDocument(publication.fileViewUrl)}
                                disabled={!publication.fileViewUrl}
                                sx={{ '&:hover': { backgroundColor: 'primary.main', color: 'white' } }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Baixar PDF" arrow>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleDownloadDocument(publication.fileDownloadUrl, publication.description)}
                                disabled={!publication.fileDownloadUrl}
                                sx={{ '&:hover': { backgroundColor: 'success.main', color: 'white' } }}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          {publications.pageCount > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4,
                mb: 2,
              }}
            >
              <MuiPagination
                count={publications.pageCount}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                disabled={loading}
              />
            </Box>
          )}
        </>
      )}
    </ListPageLayout>
  );
}
